import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  addReviewMatch,
  getMap,
  getReview,
  listCapabilities,
  listMaps,
  promoteReview,
  reviewMatch,
  reviewTool,
  signOffReview,
} from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { useAsync } from "@/lib/useAsync";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { TextField } from "@/components/ui/TextField";
import type { Confidence, ReviewStatus, Suggestions } from "@/api/types";
import { BrowseLoading, BrowsePage } from "@/components/BrowsePage";
import { SuggestionsView } from "@/components/SuggestionsView";
import { CapabilityLink } from "@/components/RegistryLinks";
import { MapFreshness } from "@/components/MapFreshness";
import { ago } from "@/lib/time";

/** How many tools and practices a map suggests, across its three sections. */
const suggestionCount = (s: Suggestions) => s.required.length + s.delivers.length + s.may_help.length;

const REVIEW_LABEL: Record<ReviewStatus, string> = {
  "not-started": "Not started",
  "in-review": "In review",
  "signed-off": "Signed off",
};

/** What this page is for, in the same voice as the other tabs' intros. */
function ReviewIntro() {
  return (
    <p className="mb-4 max-w-4xl text-sm text-text/60">
      A Staff+ engineer reviews each suggested map: accept, reject or add matches, then sign off. A signed-off map
      becomes a delivery brief.
    </p>
  );
}

/** The Staff+ pass over suggested maps, in the shared browse layout (components/BrowsePage.tsx):
 * a card per map, newest first, filterable by how far its review has got, then one map's
 * review workspace at `/review/<map id>`. */
export function ReviewPage() {
  const list = useAsync(listMaps, []);
  const { mapId } = useParams();

  if (list.status !== "ready")
    return (
      <BrowseLoading
        title="Review"
        intro={<ReviewIntro />}
        noun="maps"
        error={list.status === "error" ? list.message : undefined}
      />
    );

  const maps = list.data.maps;
  const count = (st: ReviewStatus) => maps.filter((m) => m.review_status === st).length;

  return (
    <BrowsePage
      title="Review"
      intro={<ReviewIntro />}
      meta={`${maps.length} maps`}
      items={maps}
      itemKey={(m) => m.id}
      itemPath={(m) => `/review/${encodeURIComponent(m.id)}`}
      selectedKey={mapId ?? null}
      basePath="/review"
      searchable={(m) => ({
        text: [m.title, m.verdict, REVIEW_LABEL[m.review_status], m.source_text, ...m.matches.map((x) => x.capability)]
          .join(" ")
          .toLowerCase(),
        fields: { review: [m.review_status], verdict: [m.verdict], superseded: [m.superseded_by ? "yes" : "no"] },
      })}
      storageKey="review"
      noun="maps"
      empty={<p className="text-text/60">No maps yet — run one from Map a change.</p>}
      filter={{
        options: [
          { value: "", label: "Every map" },
          ...(Object.keys(REVIEW_LABEL) as ReviewStatus[]).map((st) => ({
            value: st,
            label: `${REVIEW_LABEL[st]} (${count(st)})`,
          })),
        ],
        passes: (m, st) => m.review_status === st,
      }}
      card={(m) => (
        <Card className="flex h-full cursor-pointer flex-col hover:border-accent-line">
          <div className="mb-1 flex items-start justify-between gap-2">
            <h3 className="font-medium">{m.title}</h3>
            <div className="flex shrink-0 gap-1">
              {m.superseded_by && <Badge>superseded</Badge>}
              <Badge
                tone={
                  m.review_status === "signed-off" ? "accent" : m.review_status === "in-review" ? "neutral" : "warn"
                }
              >
                {REVIEW_LABEL[m.review_status]}
              </Badge>
            </div>
          </div>
          <p className="text-xs text-text/50">
            {m.verdict} · mapped {ago(m.catalog_read_at ?? m.created_at)}
          </p>
          <p className="mt-2 line-clamp-3 text-sm text-text/70">{m.source_text}</p>
          <p className="mt-2 text-xs text-text/50">
            {m.matches.length} capabilities
            {m.suggestions && ` · ${suggestionCount(m.suggestions)} suggestions`}
          </p>
        </Card>
      )}
      row={(m) => (
        <>
          <div className="truncate font-medium">{m.title}</div>
          <div className="text-xs text-text/50">
            {m.verdict} · {REVIEW_LABEL[m.review_status]}
          </div>
        </>
      )}
      detail={(m) => <ReviewWorkspace key={m.id} mapId={m.id} />}
    />
  );
}

/** One map's review. Shows the original request (judge matches against it, not against
 * capability names), then each suggested match with any verdict already recorded.
 *
 * Unjudged matches get Accept (one click) or Reject, which asks why — the backend refuses
 * a rejection without a reason, because the reason is the correction. "The map missed a
 * capability" adds one the mapper didn't find, offering only capabilities not already on
 * the map. "Sign off" freezes the review and publishes it as a delivery brief (the backend
 * refuses an empty review); once signed off, no verdicts or additions can be recorded,
 * "Reopen" makes it editable again, and "Preview promotion" dry-runs turning the
 * corrections into observations, without committing anything. */
function ReviewWorkspace({ mapId }: { mapId: string }) {
  const mapState = useAsync(() => getMap(mapId), [mapId]);
  const reviewState = useAsync(() => getReview(mapId), [mapId]);
  const capabilitiesState = useAsync(listCapabilities, []);
  const [newCapability, setNewCapability] = useState("");
  const [newRequirement, setNewRequirement] = useState("");
  const [newConfidence, setNewConfidence] = useState<Confidence>("plausible");
  const [error, setError] = useState<string | null>(null);
  const [promotion, setPromotion] = useState<{ planned?: unknown[]; skipped: unknown[] } | null>(null);

  if (mapState.status === "loading" || reviewState.status === "loading")
    return <p className="text-text/60">Loading…</p>;
  if (mapState.status === "error") return <p className="text-warn">{mapState.message}</p>;
  if (reviewState.status === "error") return <p className="text-warn">{reviewState.message}</p>;

  const assessment = mapState.data;
  const review = reviewState.data;
  const signedOff = review.status === "signed-off";
  const verdictOf = (capability: string) => review.match_reviews.find((m) => m.capability === capability)?.verdict;

  /** Runs one review action, then reloads the review so its new state shows; a refusal
   * from the backend (e.g. already signed off) is shown instead. */
  async function act<T>(fn: () => Promise<T>) {
    setError(null);
    try {
      await fn();
      reviewState.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
    }
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-[family-name:--font-display] text-xl">{assessment.title}</h3>
          <p className="text-sm text-text/60">
            <Badge tone={signedOff ? "accent" : "neutral"}>{review.status}</Badge> · catalog as of{" "}
            {ago(assessment.catalog_read_at ?? assessment.created_at)}
          </p>
        </div>
        {!signedOff ? (
          <Button variant="primary" onClick={() => act(() => signOffReview(mapId))}>
            Sign off
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={() => act(() => signOffReview(mapId, true))}>Reopen</Button>
            <Button
              variant="primary"
              onClick={async () => {
                setError(null);
                try {
                  setPromotion(await promoteReview(mapId, false));
                } catch (e) {
                  setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
                }
              }}
            >
              Preview promotion
            </Button>
          </div>
        )}
      </div>

      <MapFreshness mapId={mapId} />

      <blockquote className="border-l-2 border-line-mid pl-3 text-sm text-text/70">{assessment.source_text}</blockquote>

      <section>
        <h4 className="mb-2 text-xs font-semibold tracking-wide text-text/60 uppercase">Suggested matches</h4>
        <ul className="flex flex-col gap-2">
          {assessment.matches.map((m) => {
            const verdict = verdictOf(m.capability);
            return (
              <li key={m.capability} className="rounded border border-line p-3 text-sm">
                <div className="mb-1 flex items-center gap-2">
                  <CapabilityLink id={m.capability} />
                  <Badge>{m.confidence}</Badge>
                  {verdict && <Badge tone={verdict === "reject" ? "warn" : "accent"}>{verdict}</Badge>}
                </div>
                <p className="text-text/70">
                  "{m.requirement}" — {m.rule}
                </p>
                {!signedOff && !verdict && (
                  <div className="mt-2 flex gap-2">
                    <Button onClick={() => act(() => reviewMatch(mapId, m.capability, "accept"))}>Accept</Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        const reason = window.prompt("Why does this not belong?");
                        if (reason) act(() => reviewMatch(mapId, m.capability, "reject", reason));
                      }}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {!signedOff && (
        <section>
          <h4 className="mb-2 text-xs font-semibold tracking-wide text-text/60 uppercase">
            The map missed a capability
          </h4>
          {capabilitiesState.status === "error" && <p className="text-sm text-warn">{capabilitiesState.message}</p>}
          <div className="flex flex-wrap items-end gap-2">
            {capabilitiesState.status === "ready" &&
              (() => {
                const taken = new Set([
                  ...assessment.matches.map((m) => m.capability),
                  ...review.added_matches.map((m) => m.capability),
                ]);
                const options = capabilitiesState.data.capabilities
                  .filter((c) => !taken.has(c.id))
                  .sort((a, b) => a.name.localeCompare(b.name));
                return (
                  <Select
                    label="Capability"
                    value={newCapability}
                    onChange={setNewCapability}
                    placeholder="Select a capability…"
                    className="max-w-64"
                  >
                    {options.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.id})
                      </option>
                    ))}
                  </Select>
                );
              })()}
            <TextField label="Requirement" value={newRequirement} onChange={setNewRequirement} className="max-w-72" />
            <Select
              label="Confidence"
              value={newConfidence}
              onChange={(v) => setNewConfidence(v as Confidence)}
              className="max-w-36"
            >
              <option value="strong">strong</option>
              <option value="plausible">plausible</option>
              <option value="weak">weak</option>
            </Select>
            <Button
              onClick={() =>
                act(() => addReviewMatch(mapId, newCapability, newConfidence, newRequirement)).then(() => {
                  setNewCapability("");
                  setNewRequirement("");
                })
              }
              isDisabled={!newCapability || !newRequirement}
            >
              Add
            </Button>
          </div>
        </section>
      )}

      <SuggestionsView
        suggestions={assessment.suggestions}
        reviewing={{
          review,
          signedOff,
          onVerdict: (e, verdict, note) => act(() => reviewTool(mapId, e.type, e.id, verdict, note)),
        }}
      />

      {review.added_matches.length > 0 && (
        <section>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Added in review</h4>
          <ul className="text-sm">
            {review.added_matches.map((m) => (
              <li key={m.capability}>
                <CapabilityLink id={m.capability} /> <Badge>{m.confidence}</Badge> — "{m.requirement}"
              </li>
            ))}
          </ul>
        </section>
      )}

      {promotion && (
        <section className="rounded border border-accent-line bg-accent-bg p-3 text-sm">
          <h4 className="mb-1 font-semibold text-accent">Promotion plan</h4>
          <p>
            {promotion.planned?.length ?? 0} observation(s) would be created; {promotion.skipped.length} skipped.
          </p>
        </section>
      )}

      {error && <p className="text-sm text-warn">{error}</p>}
    </Card>
  );
}
